/* 
Mono
console.log('⏳ evaluating', import.meta.url);
*/

import '../css/auth.css';

// Import project modules
import { functions, httpsCallable, signInWithCustomToken, firebaseAuth } from './initFirebase.js';
import { qS, qSA, isLogin, isSignUp, isVerify, isUnsubscribe, isInstall, pageLanguage, publicPage, url, userIsLoggedIn, normalizeEmail } from './_default.js';
import { notification } from './notification.js';
import { activity } from './activity.js';
import { error } from './error.js';
import { signUp } from './signUp.js';
import { appInstallHint } from './app/appInstallHint.js';
import { appMaintenance } from './app/appMaintenance.js';
import { t } from './app/appTranslation.js';
import { getLocalStorageItem, setLocalStorageItem } from './storage.js';
import { cookieConsent } from './cookieConsent.js';
import { invitedHandler } from './invitedHandler.js';
import { redirectToSsoIfRequired, redirectToSsoPage } from './sso.js';

const auth = {
  userIsLoggedIn: userIsLoggedIn,
  init: async function() {
    console.log('auth.init');
    cookieConsent.init();

    // Invited pop up
    invitedHandler.init();

    // Init notification
    notification.init();

    // Check for maintenance
    if(appMaintenance() === true){
      console.log('Maintenance mode detected');
      return;
    }

    // Check for email and code in URL
    auth.checkForURLParamater();

    // Show install hint
    if(isLogin || isInstall){
      appInstallHint();
    }

    // Check color scheme
    // if(localStorage.getItem('colorScheme')){
    //   document.documentElement.classList.add(`${localStorage.getItem('colorScheme')}-mode`);
    // }

    // Init error handling
    error.init();

    // Show login, signup, or verify page
    if (isLogin) {
      qS('#login_email_form').addEventListener('submit', (e) => {
        console.log('login_email_form submit');
        e.preventDefault();

        this.loginRequestCode({ email: normalizeEmail(qS('#login_email').value), language: pageLanguage });
      });
      qS('#login_code_form').addEventListener('submit', (e) => {
        e.preventDefault();
        auth.confirmCode(normalizeEmail(qS('#login_email').value), qS('#login_verification_code').value.trim());
      });
      qS('#login_verification_code').addEventListener('input', (e) => {
        auth.enableButton(qS('#login_confirm'), qS('#login_verification_code'));
        if(qS('#login_verification_code').value.length === 6){
          auth.confirmCode(normalizeEmail(qS('#login_email').value), qS('#login_verification_code').value);
        }
      });
      // Remove error when typing
      qS('#login_email').addEventListener('input', () => {
        this.restart('login');
      });
    } 
    
    if (isVerify) {
      // Verify
      qS('#verify_code_form').addEventListener('submit', (e) => {
        e.preventDefault();
        auth.confirmCode(normalizeEmail(qS('#verify_email').value), qS('#verify_verification_code').value, 'sign_up');
      });
      qS('#verify_verification_code').addEventListener('input', (e) => {
        auth.enableButton(qS('#verify_confirm'), qS('#verify_verification_code'));
        if(qS('#verify_verification_code').value.length === 6){
          auth.confirmCode(normalizeEmail(qS('#verify_email').value), qS('#verify_verification_code').value, 'sign_up');
        }
      });
    }

    if (isVerify) {
      auth.showVerifyPage();
    }
    
    if (isUnsubscribe) {
      auth.showUnsubscribePage();
    }

    qS('body').classList.remove('page-loading');

    try {
      await activity.initAndTrackPageView();
    } catch (error) {
      console.error('activity.initAndTrackPageView failed:', error);
    }
  },

  checkForURLParamater: function(){
    console.log('auth.checkForURLParamater', isSignUp);
    if(isLogin && url.parameters.email){
      qS(`#login_email`).value = normalizeEmail(url.parameters.email);
    }

    if(isSignUp && url.parameters.email){
      console.log('auth.checkForURLParamater isSignUp');
      qS(`#sign_up_email`).value = normalizeEmail(url.parameters.email);
    }

    if(isUnsubscribe && url.parameters.email){
      qS('#unsubscribe_email').value = normalizeEmail(url.parameters.email);
    }

    // For split tests
    if(url.parameters.g){
      setLocalStorageItem('splitTestGroup', url.parameters.g);
    }

    if(url.parameters.code){
      qS(`#login_verification_code`).value = url.parameters.code;
      qS(`#login_email_container`).querySelector('.success').style.display = 'block';
      qS(`#login_next`).style.display = 'none';
      qS(`#login_code_form`).style.display = 'block';

      // Auto submit form
      this.confirmCode(normalizeEmail(url.parameters.email), url.parameters.code, 'sign_up');
    }
  },

  showVerifyPage: function(){
    console.log('auth.showVerifyPage');
    // User created, now verify email
    // Note: can't use url variable, because URL has changed
    const params = new URLSearchParams(window.location.search);
    const email = params.get('email') ? normalizeEmail(decodeURIComponent(params.get('email'))) : null;

    if(email){
      qS('#verify_copy_email').textContent = email;
      qS('#verify_email').value = email;

      // request login code
      auth.loginRequestCode({ email, language: pageLanguage });
    }else{
      const loginHref = publicPage?.links?.login?.href || navigator.language === 'de' ? '/einloggen' : '/login';
      const errorMessage = encodeURIComponent(publicPage?.text?.auth?.loginErrorInfo || 'There was an error, please try again.');
      window.location.href = `${loginHref}?info=${errorMessage}&info_type=error`;
    }
  },

  showUnsubscribePage: function(){
    const params = new URLSearchParams(window.location.search);
    const unsubscribeFromType = params.get('type') || 'all';

    console.log('auth.showUnsubscribePage unsubscribeFromType', unsubscribeFromType);
    qS('#unsubscribe_form').addEventListener('submit', async (e) => {
      e.preventDefault();

      qS('#unsubscribe_confirm').disabled = true;
      qS('#unsubscribe_confirm').classList.add('button-loading');
      const unsubscribeResult = await httpsCallable(functions, 'unsubscribe')({
        email: normalizeEmail(qS('#unsubscribe_email').value), 
        unsubscribeFromType,
        language: pageLanguage
      });

      qS('#unsubscribe_confirm').disabled = false;
      qS('#unsubscribe_confirm').classList.remove('button-loading');

      if(unsubscribeResult.data.success){
        qS('#unsubscribe_title').textContent = publicPage?.text?.auth?.unsubscribedTitle || 'Unsubscribed';
        qS('#unsubscribe_copy').innerHTML = publicPage?.text?.auth?.unsubscribedCopy || 'Your email address has been unsubscribed from the list.';
        qS('#unsubscribe_form').style.display = 'none';

        // Track delete
        await activity.log(activity.activityTypes.unsubscribed, {email: normalizeEmail(qS('#unsubscribe_email').value)});
      }else{
        notification.showError(unsubscribeResult.data.errorMessage);
      }
    });
  },

  loginRequestCode: async function(recipient) {
    if(isLogin){
      qS(`#login_email_form button`).classList.add('button-loading');
      qS(`#login_email_form button`).disabled = true;
    }
    let body = recipient;
    // Add language, needed for email 
    body.language = body.language || publicPage?.language || pageLanguage || 'en';

    try {
      const redirectedToSso = await redirectToSsoIfRequired({
        email: body.email,
        flow: isVerify ? 'signUp' : 'login',
        redirect: url.parameters.redirect || '/app/',
        pending: { language: body.language },
      });
      if (redirectedToSso) {
        return;
      }

      const result = await httpsCallable(functions, 'requestCode')(body);
      console.log('loginRequestCode result', result);
      if(result.data.success){
        if(isLogin){
          qS('#login_code_form').style.display = 'block';
          qS('#login_next').style.display = 'none';
          qS('#login_verification_code').focus();
        }
        await activity.log(activity.activityTypes.requestedLoginCode, null);

      }else if(result.data.error === 'SSO_REQUIRED' && result.data.sso){
        redirectToSsoPage({
          email: body.email,
          flow: isVerify ? 'signUp' : 'login',
          redirect: url.parameters.redirect || '/app/',
          sso: result.data.sso,
          pending: { language: body.language },
        });
        return;
      }else{
        error.handle(result.data, `login_email_container`);
      }

    } catch (error) {
      if(isLogin){
        qS(`#login_email_form button`).classList.remove('button-loading');
        qS(`#login_email_form button`).disabled = false;
      }
      notification.showError(error);
      console.error('Error:', error);
      throw error;
    }
    if(isLogin){
      qS(`#login_email_form button`).classList.remove('button-loading');
      qS(`#login_email_form button`).disabled = false;
    }
  },

  signInWithFirebase: function(customToken, callback, user){
    signInWithCustomToken(firebaseAuth, customToken).then((userCredential) => {
      setLocalStorageItem('loggedIn', true);
      auth.userIsLoggedIn = true;
      // Redirect
      callback(user);
    }).catch((error) => {
      const errorPrefix = publicPage?.text?.auth?.signInErrorPrefix || 'Error signing in with custom token:';
      alert(`${errorPrefix} ${error}`);
      console.error('Error signing in with custom token:', error);
    });
  },

  redirectAfterVerification: function(user) {
    qS('body').classList.add('page-loading');

    // Redirect
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');

    if(redirect){
      // If redirect is in URL
      window.location.href = redirect;

    }else{
      // All users
      window.location.href = "/app/";
    }
  },

  confirmCode: async function(recipient, verificationCode, type = null) {
    verificationCode = verificationCode.toLowerCase().trim();
    
    qSA(`.code-form button`).forEach(button => {
      button.classList.add('button-loading');
      button.disabled = true;
    });

    const publicId = getLocalStorageItem('publicId') || null;
    const body = {
      ...{email: recipient},
      verificationCode,
      publicId
    };

    try {
      console.log('confirmCode request, body:', body);
      const result = await httpsCallable(functions, 'confirmCode')(body);
      console.log('confirmCode result:', result);
      if (result.data.success) {
        await activity.log(activity.activityTypes.login, null);
        
        // Sign in and redirect
        auth.signInWithFirebase(result.data.customToken, auth.redirectAfterVerification, result.data.user);
      } else {
        qS(`.code-form button`).classList.remove('button-loading');
        qS(`.code-form button`).disabled = false;

        if(result.data.reset){
          if(isLogin) {
            notification.showError(publicPage?.text?.auth?.tooManyAttempts || 'The code was entered incorrectly too many times. Please request a new code.');
            this.restart('login');
          }else{
            const loginHref = publicPage?.links?.login?.href || navigator.language === 'de' ? '/einloggen' : '/login';
            const info = encodeURIComponent(publicPage?.text?.auth?.tooManyAttempts || 'The code was entered incorrectly too many times. Please request a new code.');
            window.location.href = `${loginHref}?email=${encodeURIComponent(recipient)}&info=${info}&info_type=error`;
          }
        }else{
          error.handle(result.data, `login_verification_code_container`);
          error.handle(result.data, `verify_verification_code_container`);
        }
      }

    } catch (error) {
      qS(`.code-form button`).classList.remove('button-loading');
      qS(`.code-form button`).disabled = false;
      const errorPrefix = publicPage?.text?.auth?.genericRequestErrorPrefix || 'Error:';
      notification.showError(`${errorPrefix} ${error}`);
      console.error('Error:', error);
      throw error;
    }
  },

  restart: function(formType) {
    const formContainer = qS(`#${formType}_email_container`);
    formContainer.querySelector('.success').style.display = 'none';
    formContainer.classList.remove('input-error');
    error.handle({success: false}, `${formType}_verification_code_container`);
    qS(`#${formType}_next`).style.display = 'block';
    qS(`#${formType}_code_form`).style.display = 'none';
    qS(`#${formType}_verification_code`).value = '';
  },

  enableButton: function(button, input) {
    button.disabled = input.value.length !== 6;
  },

  updateUIAfterCodeSent: function(result) {
    const container = qS(`#login_email_container`);
    if (result.success) {
      container.querySelector('.success').style.display = 'block';
      qS(`#login_next`).style.display = 'none';
      qS(`#login_code_form`).style.display = 'block';
    } else {
      error.handle(result, `#login_email_container`);
    }
  }
}

window.auth = auth;

let authInitStarted = false;

const startAuth = () => {
  if (authInitStarted) {
    return;
  }

  authInitStarted = true;
  auth.init().catch((error) => {
    console.error('auth.init failed:', error);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startAuth, { once: true });
} else {
  startAuth();
}

window.addEventListener('pageshow', startAuth);

export { auth };
